import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { MenuItem } from '@material-ui/core';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { FaUserTie } from 'react-icons/fa';

import Paper from 'components/Paper';
import TextField from 'components/TextField';
import Button from 'components/Button';
import EmbarcadoresActions from 'store/ducks/embarcadores';
import { embarcadoresAPI } from 'services/apiRest';

import { Container, WrapperBody, WrapperContent, WrapperBottom, WrapperInput, InputRow } from './styles';

const validationSchema = Yup.object().shape({
  nome: Yup.string().required('Nome é obrigatório'),
  cpfCnpj: Yup.string().required('CPF/CNPJ é obrigatório'),
});

export default function EmbarcadorForm() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();
  const isEdit = !!id;
  const isView = window.location.pathname.includes('/view/');

  const { embarcadores } = useSelector((state) => state.embarcadores);
  const [embarcadorCarregado, setEmbarcadorCarregado] = useState(null);

  useEffect(() => {
    if ((isEdit || isView) && id && !embarcadorCarregado) {
      embarcadoresAPI
        .buscarPorId(id)
        .then((e) => setEmbarcadorCarregado(e))
        .catch(() => {
          // fallback
        });
    } else if (!isEdit && embarcadores.length === 0) {
      dispatch(EmbarcadoresActions.getEmbarcadoresRequest());
    }
  }, [dispatch, embarcadorCarregado, embarcadores.length, id, isEdit, isView]);

  const embarcador = useMemo(() => {
    if (isEdit || isView) {
      return embarcadorCarregado || embarcadores.find((e) => String(e.id) === String(id)) || null;
    }
    return null;
  }, [embarcadorCarregado, embarcadores, id, isEdit, isView]);

  const formik = useFormik({
    initialValues: {
      nome: embarcador?.nome || '',
      nomeFantasia: embarcador?.nomeFantasia || '',
      cpfCnpj: embarcador?.cpfCnpj || '',
      inscricaoEstadual: embarcador?.inscricaoEstadual || '',
      tipoPessoa: embarcador?.tipoPessoa || 'PJ',
      cep: embarcador?.cep || '',
      endereco: embarcador?.endereco || '',
      numero: embarcador?.numero || '',
      complemento: embarcador?.complemento || '',
      bairro: embarcador?.bairro || '',
      cidade: embarcador?.cidade || '',
      uf: embarcador?.uf || '',
      email: embarcador?.email || '',
      numeroContato: embarcador?.numeroContato || '',
      nomeContato: embarcador?.nomeContato || '',
      dataInclusao: embarcador?.dataInclusao || new Date().toISOString().split('T')[0],
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      const payload = {
        ...values,
        cpfCnpj: (values.cpfCnpj || '').replace(/\D/g, ''),
        cep: (values.cep || '').replace(/\D/g, ''),
      };

      const handleSuccess = () => history.push('/embarcadores/list');

      if (isEdit) {
        dispatch(EmbarcadoresActions.updateEmbarcadorRequest({ id, ...payload }, handleSuccess));
      } else {
        dispatch(EmbarcadoresActions.addEmbarcadorRequest(payload, handleSuccess));
      }
    },
  });

  return (
    <Container>
      <Paper
        title={isView ? 'VISUALIZAR EMBARCADOR' : isEdit ? 'EDITAR EMBARCADOR' : 'NOVO EMBARCADOR'}
        icon={<FaUserTie size={24} style={{ color: '#122d58', marginRight: '25px' }} />}
        rightContent={
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Button
              label="Voltar"
              onClick={() => history.push('/embarcadores/list')}
              type="button"
              width="120px"
            />
            {!isView && (
              <Button
                label={isEdit ? 'Atualizar' : 'Salvar'}
                type="button"
                width="120px"
                onClick={() => formik.handleSubmit()}
              />
            )}
          </div>
        }
      >
        <WrapperBody>
          <WrapperContent>
            <InputRow>
              <WrapperInput>
                <TextField
                  label="Nome"
                  name="nome"
                  value={formik.values.nome}
                  onChange={formik.handleChange}
                  error={formik.touched.nome && Boolean(formik.errors.nome)}
                  helperText={formik.touched.nome && formik.errors.nome}
                  disabled={isView}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
              <WrapperInput>
                <TextField
                  label="Nome Fantasia"
                  name="nomeFantasia"
                  value={formik.values.nomeFantasia}
                  onChange={formik.handleChange}
                  disabled={isView}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </InputRow>

            <InputRow>
              <WrapperInput>
                <TextField
                  label="CPF/CNPJ"
                  name="cpfCnpj"
                  value={formik.values.cpfCnpj}
                  onChange={formik.handleChange}
                  error={formik.touched.cpfCnpj && Boolean(formik.errors.cpfCnpj)}
                  helperText={formik.touched.cpfCnpj && formik.errors.cpfCnpj}
                  disabled={isView}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
              <WrapperInput>
                <TextField
                  label="Tipo de Pessoa"
                  name="tipoPessoa"
                  select
                  value={formik.values.tipoPessoa}
                  onChange={formik.handleChange}
                  disabled={isView}
                  fullWidth
                  color="secondary"
                >
                  <MenuItem value="PF">Pessoa Física</MenuItem>
                  <MenuItem value="PJ">Pessoa Jurídica</MenuItem>
                </TextField>
              </WrapperInput>
            </InputRow>

            <InputRow>
              <WrapperInput>
                <TextField
                  label="Inscrição Estadual"
                  name="inscricaoEstadual"
                  value={formik.values.inscricaoEstadual}
                  onChange={formik.handleChange}
                  disabled={isView}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
              <WrapperInput>
                <TextField
                  label="E-mail"
                  name="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  disabled={isView}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </InputRow>

            <InputRow>
              <WrapperInput>
                <TextField
                  label="CEP"
                  name="cep"
                  value={formik.values.cep}
                  onChange={formik.handleChange}
                  disabled={isView}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
              <WrapperInput>
                <TextField
                  label="Endereço"
                  name="endereco"
                  value={formik.values.endereco}
                  onChange={formik.handleChange}
                  disabled={isView}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </InputRow>

            <InputRow>
              <WrapperInput>
                <TextField
                  label="Número"
                  name="numero"
                  value={formik.values.numero}
                  onChange={formik.handleChange}
                  disabled={isView}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
              <WrapperInput>
                <TextField
                  label="Complemento"
                  name="complemento"
                  value={formik.values.complemento}
                  onChange={formik.handleChange}
                  disabled={isView}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </InputRow>

            <InputRow>
              <WrapperInput>
                <TextField
                  label="Bairro"
                  name="bairro"
                  value={formik.values.bairro}
                  onChange={formik.handleChange}
                  disabled={isView}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
              <WrapperInput>
                <TextField
                  label="Cidade"
                  name="cidade"
                  value={formik.values.cidade}
                  onChange={formik.handleChange}
                  disabled={isView}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </InputRow>

            <InputRow>
              <WrapperInput>
                <TextField
                  label="UF"
                  name="uf"
                  value={formik.values.uf}
                  onChange={formik.handleChange}
                  disabled={isView}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
              <WrapperInput>
                <TextField
                  label="Data Inclusão"
                  name="dataInclusao"
                  type="date"
                  value={formik.values.dataInclusao}
                  onChange={formik.handleChange}
                  disabled={isView}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  color="secondary"
                />
              </WrapperInput>
            </InputRow>

            <InputRow>
              <WrapperInput>
                <TextField
                  label="Telefone"
                  name="numeroContato"
                  value={formik.values.numeroContato}
                  onChange={formik.handleChange}
                  disabled={isView}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
              <WrapperInput>
                <TextField
                  label="Nome do Contato"
                  name="nomeContato"
                  value={formik.values.nomeContato}
                  onChange={formik.handleChange}
                  disabled={isView}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </InputRow>
          </WrapperContent>
        </WrapperBody>
      </Paper>
    </Container>
  );
}


